import { useRef, useEffect } from "react";
import { Title } from "@vds/typography";
import { Icon } from "@vds/icons";
import { ButtonGroup } from "@vds/buttons";
import {ModalOverlay, ModalContainer, ModalBodyContainer, ScrollableContent} from "../../../pages/Dashboard/styles";
import { CameraCancelSubmitFrame } from "../../../features/camerOnboarding/discoveredCameraOnboarding/styles";
import styles from "./BaseModal.module.css";

const stickyHeaderFooterStyle = {
  position: "sticky",
  background: "#fff",
  zIndex: 2,
};

const BaseModal = (props) => {
  const scrollContainerRef = useRef(null);
  const scrollPosRef = useRef(0);
  const modalRef = useRef(null);

  useEffect(() => {
    if (!props.opened) return;

    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, [props.opened]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      scrollPosRef.current = container.scrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !props.disableOutsideClick) {
      props.onClose?.();
    }
  };

  const handleContentClick = (e) => {
    // Prevent scroll restoration on click
    e.stopPropagation();
  };

  if (!props.opened) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer 
        ref={modalRef}
        width={props.width}
        $height={props.height}
        $maxWidth={props.maxWidth}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button (a custom element replaces the default one) */}
        {!props.hideCloseButton && props.closeButton}
        {!props.hideCloseButton && !props.closeButton && (
          <div
            className={`${styles.openModalCloseButton} ${props.closeButtonClass || ""}`}
            onClick={props?.onClose}
          >
            <Icon name="close" />
          </div>
        )}

        <ModalBodyContainer>
          {/* Header */}
          {props.showModalTitle && (
            <div>
              <h3>{props.title}</h3>
            </div>
          )}

          {!props.showModalTitle && props.title && (
            <div className={styles.titleContainer}>
              <Title size="large" bold={true}>
                {props.title}
              </Title>
            </div>
          )}

          {/* Scrollable Content */}
          <ScrollableContent 
            ref={scrollContainerRef}
            onClick={handleContentClick}
          >
            <div className={`open-modal-scroll ${styles.scrollContentWrapper}`}>
              {props?.children}
            </div>
          </ScrollableContent>

          {/* Footer */}
          <div className={`modalButtonContainer ${styles.modalButtonContainer}`}>
            {!props.hideButtons && (
              <CameraCancelSubmitFrame>
                <ButtonGroup
                  childWidth={props.buttonWidth ? props.buttonWidth : "100%"}
                  viewport={"desktop"}
                  rowQuantity={{ desktop: 2 }}
                  data={[
                    {
                      children: props?.closeButtonText,
                      size: props?.size || "large",
                      use: "primary",
                      disabled: props?.backdisabled,
                      onClick: props?.onBack,
                    },
                    {
                      children: props?.primaryButtonText,
                      size: "large",
                      use: "secondary",
                      onClick: props.onCloseButtonClick
                        ? props.onCloseButtonClick
                        : props.onClose,
                      disabled: props?.disabled,
                    },
                  ]}
                />
              </CameraCancelSubmitFrame>
            )}
            {props.hideButtons && (
              <CameraCancelSubmitFrame>
                <ButtonGroup
                  childWidth={props.buttonWidth ? props.buttonWidth : "100%"}
                  viewport={"desktop"}
                  rowQuantity={{ desktop: 2 }}
                  data={[
                    {
                      children: props?.closeButtonText,
                      size: props?.size || "medium",
                      use: "primary",
                      disabled: props?.backdisabled,
                      onClick: props?.onBack,
                    },
                  ]}
                />
              </CameraCancelSubmitFrame>
            )}
          </div>
        </ModalBodyContainer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default BaseModal;